import parse, { HTMLReactParserOptions } from 'html-react-parser'
import React, { useEffect, useState } from 'react'

import { cleanString } from '../../utils/helpers'
import JumpLinks from '../JumpLinks'
import Lightbox from '../Lightbox'
import ShareLinks from '../ShareLinks'
import DownloadCard from '../UI/DownloadCard'
import GridContainer from '../UI/GridContainer'
import PromoCardHorizontal from '../UI/PromoCardHorizontal'
import Testimonial from '../UI/Testimonial'
import Wrapper from '../UI/Wrapper'
import {
  ArticleContainer,
  BodySection,
  DesktopDownloadContainer,
  MobileDownloadContainer,
  ShareContainer,
  StickySidebar,
} from './styles'

interface PageBodyProps {
  content: {
    show_download_card?: 'True' | 'False'
    show_download_cta_button?: 'True' | 'False'
    show_sticky_nav?: 'True' | 'False'
    image_align?: 'Left' | 'Right' | string | null
    topic: any[]
    page_break?: any[]
    showShareLinks?: boolean
    long_description?: boolean
  }
  downloadUrl?: string
  downloadResource?: any
  noTopPadding?: boolean
  noBottomPadding?: boolean
}

const options: HTMLReactParserOptions = {
  replace: (domNode) => {
    const [lightboxImage, setLightboxImage] = useState<string | null>(null)

    if (domNode.attribs) {
      if (domNode.name === 'img') {
        const src = domNode.attribs.src

        return (
          <div>
            <Lightbox
              lightboxImage={lightboxImage}
              setLightboxImage={setLightboxImage}
            />
            <img
              alt={domNode.attribs.alt}
              loading="lazy"
              onClick={() => setLightboxImage(`${src}?auto=webp&width=1920`)}
              src={`${src}?auto=webp&width=1920`}
            />
          </div>
        )
      }
    }

    return undefined
  },
}

const PageBody = (props: PageBodyProps) => {
  const {
    show_download_card = 'False',
    show_download_cta_button = 'False',
    show_sticky_nav = 'False',
    image_align,
    topic,
    page_break = [],
    showShareLinks = false,
    long_description = '',
  } = props?.content

  const body = long_description || topic?.[0]?.long_description || ''
  const links: { target: string; text: string }[] = []
  const [currentActiveLink, setCurrentActiveLink] = useState('')

  const topicDownloadResource =
    topic?.[0]?.download_resource ||
    topic?.[0]?.cta?.find(cta => cta.cta_resource)?.cta_resource?.download_resource

  const downloadCardContent = props.downloadResource?.linkTarget
    ? props.downloadResource
    : {
        headline: topicDownloadResource?.[0]?.headline,
        feature_image: topicDownloadResource?.[0]?.feature_image,
        linkTarget:
        topicDownloadResource?.[0]?.linkTarget ?? props?.downloadUrl,
        linkText: topicDownloadResource?.[0]?.linkText,
        side_cta_short_description:
        topicDownloadResource?.[0]?.side_cta_short_description,
      }

  const noTopPadding = Boolean(props?.noTopPadding)
  const noBottomPadding = Boolean(props?.noBottomPadding)

  const replacer = (
    match: string,
    p1: string,
    p2: string,
    p3: string,
    offset: number,
    string: string,
  ) => {
    const id = p2.toLowerCase().replace(/\W/g, '-')
    links.push({
      target: id,
      text: cleanString(p2),
    })
    return `<h2 id="${id}" class="jumpLinkSubhead">${p2}</h2>`
  }

  const formatBody = (body: string) => {
    // change the h3 to h2 with Regex in the body.
    const reg = new RegExp(/(<\/?h)(3)/, 'ig')
    const updatedBody = body.replace(reg, '$12')

    // for every h2 in the body, we add an id attribute for the jump links to target
    const newBody = updatedBody.replace(/(\<h2\>)(.*?)(\<\/h2\>)/g, replacer)

    // if there's a page break specified, and there is more than one h2,
    // then split the content in two before the final h2
    const count = (newBody.match(/<h2/g) || []).length

    if (
      (page_break?.[0]?.testimonial?.[0] || page_break?.[0]?.product) &&
      count > 0
    ) {
      const position = newBody.lastIndexOf('<h2')
      return [newBody.slice(0, position), newBody.slice(position)]
    }

    return [newBody]
  }

  const observerCallback = (entries: any) => {
    entries.forEach((entry: any) => {
      if (entry.isIntersecting) {
        setCurrentActiveLink(entry.target.id)
      }
    })
  }

  const observerOptions = {
    root: null,
    rootMargin: '0px 0px -66% 0px',
    threshold: 0.5,
  }

  useEffect(() => {
    const observer = new IntersectionObserver(observerCallback, observerOptions)
    document.querySelectorAll('.jumpLinkSubhead').forEach((heading) => {
      observer.observe(heading)
    })

    return () => {
      document.querySelectorAll('.jumpLinkSubhead').forEach((heading) => {
        observer.unobserve(heading)
      })
    }
  })

  useEffect(() => {
    history.scrollRestoration = 'manual'

    const hash = window.location.hash
    if (hash && hash !== '#') {
      Object.assign(document.createElement('a'), { href: hash }).click()
    }
  }, [])

  const formattedBody = formatBody(body)
  const parsedContent = formattedBody?.[0]
    ? parse(formattedBody[0], options)
    : null

  return (
    <BodySection noBottomPadding={noBottomPadding} noTopPadding={noTopPadding}>
      <Wrapper>
        <GridContainer>
          <StickySidebar>
            <aside>
              {show_sticky_nav === 'True' && (
                <JumpLinks
                  currentActiveLink={currentActiveLink}
                  links={links}
                />
              )}
              {show_download_card === 'True' && downloadCardContent ? (
                <DesktopDownloadContainer>
                  <DownloadCard
                    body={downloadCardContent.headline}
                    featureImage={downloadCardContent.feature_image}
                    linkTarget={downloadCardContent.linkTarget || '/'}
                    linkText={downloadCardContent.linkText}
                    shortDescription={
                      downloadCardContent.side_cta_short_description
                    }
                  />
                </DesktopDownloadContainer>
              ) : null}
            </aside>
          </StickySidebar>
          <ArticleContainer align={image_align}>
            {parsedContent}
          </ArticleContainer>
        </GridContainer>
        {page_break?.[0]?.testimonial?.[0] ? (
          <Testimonial
            citation={page_break?.[0]?.testimonial?.[0]?.source}
            testimonial={page_break?.[0]?.testimonial?.[0]?.testimonial}
          />
        ) : null}
        {page_break?.[0]?.product ? (
          <Wrapper>
            <PromoCardHorizontal
              body={page_break?.[0]?.product?.short_description}
              featureImage={page_break?.[0]?.product?.feature_image}
              headline={page_break?.[0]?.product?.headline}
              linkTarget={page_break?.[0]?.product?.linkTarget}
              linkText={page_break?.[0]?.product?.linkText}
            />
          </Wrapper>
        ) : null}
        <GridContainer>
          {formattedBody?.[1] ? (
            <ArticleContainer>{parse(formattedBody[1])}</ArticleContainer>
          ) : null}
          {showShareLinks ? (
            <ShareContainer>
              <ShareLinks />
            </ShareContainer>
          ) : null}
          {show_download_cta_button === 'True' && downloadCardContent ? (
            <MobileDownloadContainer>
              <DownloadCard
                body={downloadCardContent.headline}
                featureImage={downloadCardContent.feature_image}
                linkTarget={downloadCardContent.linkTarget || '/'}
                linkText={downloadCardContent.linkText}
                shortDescription={
                  downloadCardContent.side_cta_short_description
                }
              />
            </MobileDownloadContainer>
          ) : null}
        </GridContainer>
      </Wrapper>
    </BodySection>
  )
}

export default PageBody
